// Human-friendly timestamp formatting for the Builds list, so a build's
// completion time and age are visible at a glance.

/** ISO string for a <time dateTime> attribute. Empty for a missing timestamp. */
export function isoTime(ms: number): string {
  if (!ms || ms <= 0) return "";
  return new Date(ms).toISOString();
}

/** Absolute local timestamp, e.g. "Jun 2, 2026, 10:42". Empty for missing. */
export function absoluteTime(ms: number): string {
  if (!ms || ms <= 0) return "";
  try {
    return new Date(ms).toLocaleString(undefined, {
      timeZone: "Europe/Prague", // always show Prague time, not the viewer's local zone
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return new Date(ms).toISOString();
  }
}

/**
 * Compact elapsed-duration label for the live "thinking" timer, e.g. "0s",
 * "12s", "1m 05s", "1h 02m". Seconds-resolution under a minute (matches how
 * Claude Code shows its working timer); pads the trailing unit once it rolls
 * over so the width stays steady as it ticks. Negative/NaN clamps to "0s".
 */
export function formatDuration(ms: number): string {
  const totalSec = Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 1000)) : 0;
  if (totalSec < 60) return `${totalSec}s`;
  const totalMin = Math.floor(totalSec / 60);
  if (totalMin < 60) {
    const sec = totalSec % 60;
    return `${totalMin}m ${sec.toString().padStart(2, "0")}s`;
  }
  const hr = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  return `${hr}h ${min.toString().padStart(2, "0")}m`;
}

/** Relative age of a timestamp, e.g. "just now", "3 hours ago", "2 days ago". */
export function relativeAge(fromMs: number, nowMs: number = Date.now()): string {
  if (!fromMs || fromMs <= 0) return "";
  const diff = Math.max(0, nowMs - fromMs);
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? "" : "s"} ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk} week${wk === 1 ? "" : "s"} ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
  // Clamp to at least 1: days 360-364 give mo=12 (so the months branch is
  // skipped) but floor(day/365)=0, which would wrongly render "0 years ago".
  const yr = Math.max(1, Math.floor(day / 365));
  return `${yr} year${yr === 1 ? "" : "s"} ago`;
}
